/**
 * Shapes returned by `GET /dice/notifications/:id/deliveries`, plus the pure
 * filtering and export helpers behind the delivery report. Kept free of React
 * and axios so it can be unit tested directly.
 */

export enum DeliveryStatus {
  QUEUED = 'queued',
  SENT = 'sent',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  BOUNCED = 'bounced',
  UNDELIVERABLE = 'undeliverable',
}

export enum RecipientDeliveryState {
  ACKNOWLEDGED = 'acknowledged',
  DELIVERED = 'delivered',
  SENT = 'sent',
  FAILED = 'failed',
  UNREACHABLE = 'unreachable',
}

export type DeliveryAttemptDto = {
  channel: string;
  escalationStep: number;
  status: DeliveryStatus;
  destination: string | null;
  error: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  respondedAt: string | null;
  responseValue: string | null;
};

export type FollowUpStatus = 'sent' | 'waiting' | 'not_released';

export const FOLLOW_UP_STATUS_LABEL: Record<FollowUpStatus, string> = {
  sent: 'Sent',
  waiting: 'Waiting',
  not_released: 'Not released',
};

export type NotificationRecipientDto = {
  userGuid: string;
  name: string;
  email: string | null;
  teams: { id: string; name: string }[];
  state: RecipientDeliveryState;
  acknowledgedAt: string | null;
  respondedAt: string | null;
  followUpStatus?: FollowUpStatus | null;
  attempts: DeliveryAttemptDto[];
};

export type NotificationDeliverySummaryDto = {
  recipients: number;
  sent: number;
  delivered: number;
  acknowledged: number;
  failed: number;
  unreachable: number;
  awaitingResponse: number;
};

export type NotificationDeliveryReportDto = {
  notificationId: string;
  acknowledgementRequired: boolean;
  hasFollowUp?: boolean;
  summary: NotificationDeliverySummaryDto;
  recipients: NotificationRecipientDto[];
};

export const RECIPIENT_STATE_LABEL: Record<RecipientDeliveryState, string> = {
  [RecipientDeliveryState.ACKNOWLEDGED]: 'Responded',
  [RecipientDeliveryState.DELIVERED]: 'Delivered',
  [RecipientDeliveryState.SENT]: 'Sent',
  [RecipientDeliveryState.FAILED]: 'Failed',
  [RecipientDeliveryState.UNREACHABLE]: 'No contact info',
};

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  [DeliveryStatus.QUEUED]: 'Queued',
  [DeliveryStatus.SENT]: 'Sent',
  [DeliveryStatus.DELIVERED]: 'Delivered',
  [DeliveryStatus.FAILED]: 'Failed',
  [DeliveryStatus.BOUNCED]: 'Bounced',
  [DeliveryStatus.UNDELIVERABLE]: 'No contact info',
};

/**
 * An undeliverable attempt is only sometimes a data-quality problem: a
 * recipient who replied STOP is a compliance outcome the sender cannot fix by
 * editing a profile, so the two read differently.
 *
 * Keyed on the leading phrase written by `unreachableReason` in coms; the two
 * lists must change together.
 */
export enum UnreachableReason {
  OPTED_OUT = 'optedOut',
  NO_CONSENT = 'noConsent',
  NO_ADDRESS = 'noAddress',
}

const UNREACHABLE_PREFIXES: [string, UnreachableReason][] = [
  ['Opted out', UnreachableReason.OPTED_OUT],
  ['Has not opted in', UnreachableReason.NO_CONSENT],
  ['No address on file', UnreachableReason.NO_ADDRESS],
];

export const UNREACHABLE_REASON_LABEL: Record<UnreachableReason, string> = {
  [UnreachableReason.OPTED_OUT]: 'Opted out',
  [UnreachableReason.NO_CONSENT]: 'Not opted in',
  [UnreachableReason.NO_ADDRESS]: 'No contact info',
};

export function unreachableReasonOf(
  error: string | null
): UnreachableReason | null {
  if (!error) return null;
  return (
    UNREACHABLE_PREFIXES.find(([prefix]) => error.startsWith(prefix))?.[1] ??
    null
  );
}

/**
 * The reason to show for a recipient whose every attempt was undeliverable.
 * Mixed reasons across channels fall back to the generic label rather than
 * picking one channel's story to stand for all of them.
 */
export function recipientUnreachableReason(
  recipient: NotificationRecipientDto
): UnreachableReason | null {
  const reasons = new Set(
    recipient.attempts
      .filter((attempt) => attempt.status === DeliveryStatus.UNDELIVERABLE)
      .map((attempt) => unreachableReasonOf(attempt.error))
      .filter((reason): reason is UnreachableReason => reason !== null)
  );

  return reasons.size === 1 ? [...reasons][0] : null;
}

/**
 * Refines `Failed`/`No contact info` with the reason, when every channel agrees
 * on one.
 */
export function recipientStateLabel(
  recipient: NotificationRecipientDto
): string {
  if (recipient.state !== RecipientDeliveryState.UNREACHABLE)
    return RECIPIENT_STATE_LABEL[recipient.state];
  const reason = recipientUnreachableReason(recipient);
  return reason
    ? UNREACHABLE_REASON_LABEL[reason]
    : RECIPIENT_STATE_LABEL[recipient.state];
}

export function attemptStatusLabel(attempt: DeliveryAttemptDto): string {
  if (attempt.status === DeliveryStatus.UNDELIVERABLE) {
    const reason = unreachableReasonOf(attempt.error);
    if (reason) return UNREACHABLE_REASON_LABEL[reason];
  }
  return DELIVERY_STATUS_LABEL[attempt.status] ?? attempt.status;
}

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  inApp: 'In-App',
  teams: 'Teams',
  ttsHome: 'Home Phone',
  ttsCell: 'Cell Phone',
};

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

/**
 * Which option the recipient chose, e.g. "Responding".
 *
 * Only inbound SMS carries a value: the in-app button and the Teams card record
 * an acknowledgement without a choice attached, so those show as responded with
 * no option. Reads across attempts because the reply arrives on whichever
 * channel the recipient answered on.
 */
export function recipientResponse(
  recipient: NotificationRecipientDto
): string | null {
  return (
    recipient.attempts.find((attempt) => attempt.responseValue)
      ?.responseValue ?? null
  );
}

/**
 * Sentinel for the "no filter applied" option, since '' is a valid Select
 * value.
 */
export const ALL_FILTER = 'all';

export type DeliveryFilters = {
  search: string;
  channel: string;
  state: string;
  teamId: string;
};

export const EMPTY_DELIVERY_FILTERS: DeliveryFilters = {
  search: '',
  channel: ALL_FILTER,
  state: ALL_FILTER,
  teamId: ALL_FILTER,
};

/**
 * Channels that were actually attempted, so the filter never offers an option
 * that would return nothing.
 */
export function channelOptions(
  recipients: NotificationRecipientDto[]
): string[] {
  const channels = new Set<string>();
  recipients.forEach((recipient) =>
    recipient.attempts.forEach((attempt) => channels.add(attempt.channel))
  );
  return [...channels].sort((a, b) =>
    channelLabel(a).localeCompare(channelLabel(b))
  );
}

export function teamOptions(
  recipients: NotificationRecipientDto[]
): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  recipients.forEach((recipient) =>
    recipient.teams.forEach((team) => byId.set(team.id, team.name))
  );
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function stateOptions(
  recipients: NotificationRecipientDto[]
): RecipientDeliveryState[] {
  const states = new Set(recipients.map((recipient) => recipient.state));
  // Fixed order, worst last, rather than whatever order the rows arrived in.
  return [
    RecipientDeliveryState.ACKNOWLEDGED,
    RecipientDeliveryState.DELIVERED,
    RecipientDeliveryState.SENT,
    RecipientDeliveryState.FAILED,
    RecipientDeliveryState.UNREACHABLE,
  ].filter((state) => states.has(state));
}

/**
 * Search matches the recipient and every address we tried, so pasting a phone
 * number or an email from a complaint finds the person who complained.
 */
export function filterRecipients(
  recipients: NotificationRecipientDto[],
  filters: DeliveryFilters
): NotificationRecipientDto[] {
  const term = filters.search.trim().toLowerCase();

  return recipients.filter((recipient) => {
    if (filters.state !== ALL_FILTER && recipient.state !== filters.state)
      return false;

    if (filters.channel !== ALL_FILTER) {
      if (
        !recipient.attempts.some(
          (attempt) => attempt.channel === filters.channel
        )
      )
        return false;
    }

    if (filters.teamId !== ALL_FILTER) {
      if (!recipient.teams.some((team) => team.id === filters.teamId))
        return false;
    }

    if (!term) return true;

    const haystack = [
      recipient.name,
      recipient.email ?? '',
      ...recipient.teams.map((team) => team.name),
      recipientStateLabel(recipient),
      recipientResponse(recipient) ?? '',
      ...recipient.attempts.flatMap((attempt) => [
        channelLabel(attempt.channel),
        attemptStatusLabel(attempt),
        attempt.destination ?? '',
        attempt.error ?? '',
      ]),
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(term);
  });
}

const CSV_HEADERS = [
  'Recipient',
  'Email',
  'Teams',
  'Status',
  'Response',
  'Responded At',
  'Channel',
  'Channel Status',
  'Destination',
  'Sent At',
  'Error',
] as const;

/** RFC 4180: quote everything, and double any embedded quote. */
function csvCell(value: string | null): string {
  return `"${(value ?? '').replace(/"/g, '""')}"`;
}

/**
 * One line per delivery attempt rather than per recipient, because the audit
 * question is usually "was this person reached on this channel". Recipients
 * with no attempt at all still get a line so they are not silently absent.
 */
export function toDeliveryCsv(recipients: NotificationRecipientDto[]): string {
  const lines = [CSV_HEADERS.join(',')];

  recipients.forEach((recipient) => {
    const shared = [
      recipient.name,
      recipient.email,
      recipient.teams.map((team) => team.name).join('; '),
      recipientStateLabel(recipient),
      recipientResponse(recipient),
      recipient.respondedAt ?? recipient.acknowledgedAt,
    ];

    if (recipient.attempts.length === 0) {
      lines.push([...shared, '', '', '', '', ''].map(csvCell).join(','));
      return;
    }

    recipient.attempts.forEach((attempt) => {
      lines.push(
        [
          ...shared,
          channelLabel(attempt.channel),
          attemptStatusLabel(attempt),
          attempt.destination,
          attempt.sentAt,
          attempt.error,
        ]
          .map(csvCell)
          .join(',')
      );
    });
  });

  return lines.join('\n');
}

export type SummaryCard = {
  label: string;
  value: number;
  /**
   * Drives the badge colour; `warn` and `bad` are the ones a sender must act
   * on.
   */
  tone: 'neutral' | 'good' | 'warn' | 'bad';
};

/**
 * `Reached` counts handoffs to a provider; `Delivered` counts arrival being
 * confirmed, either by a provider receipt or by the recipient answering.
 *
 * Delivered is hidden while it merely restates Responded, which is the case on
 * deployments with no public callback URL and on Microsoft Graph email, neither
 * of which reports receipts. Showing a permanently-equal pair of cards would
 * imply the gap between them was zero rather than unmeasured.
 *
 * Opt-outs are split off the unreachable count when `recipients` is supplied,
 * because they are the one part of that total a sender cannot fix.
 */
export function summaryCards(
  summary: NotificationDeliverySummaryDto,
  acknowledgementRequired: boolean,
  recipients: NotificationRecipientDto[] = []
): SummaryCard[] {
  const cards: SummaryCard[] = [
    { label: 'Recipients', value: summary.recipients, tone: 'neutral' },
    { label: 'Reached', value: summary.sent, tone: 'good' },
  ];

  if (summary.delivered > summary.acknowledged) {
    cards.push({ label: 'Delivered', value: summary.delivered, tone: 'good' });
  }

  if (acknowledgementRequired) {
    cards.push(
      { label: 'Responded', value: summary.acknowledged, tone: 'good' },
      {
        label: 'Awaiting response',
        value: summary.awaitingResponse,
        tone: 'warn',
      }
    );
  }

  const optedOut = recipients.filter(
    (recipient) =>
      recipientUnreachableReason(recipient) === UnreachableReason.OPTED_OUT
  ).length;

  cards.push({ label: 'Failed', value: summary.failed, tone: 'bad' });

  if (optedOut > 0) {
    cards.push({ label: 'Opted out', value: optedOut, tone: 'warn' });
  }

  cards.push({
    label: 'No contact info',
    value: Math.max(summary.unreachable - optedOut, 0),
    tone: 'bad',
  });

  return cards;
}
